<template>
  <v-card>
    <v-card-title>
      <v-icon class="mr-2">mdi-cloud-upload-outline</v-icon>
      Self uploaded videos
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search videos"
        single-line
        hide-details
        clearable
        style="max-width: 320px"
      />
      <v-btn
        class="ml-3"
        color="primary"
        outlined
        :loading="loading"
        @click="fetchVideos"
      >
        <v-icon left>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </v-card-title>

    <v-card-text v-if="!hasAccess">
      <v-alert type="error" text dense>
        This page is only available to aku@kettunen.com.
      </v-alert>
    </v-card-text>

    <v-card-text v-else>
      <v-alert v-if="errorMessage" type="error" text dense>
        {{ errorMessage }}
      </v-alert>

      <div class="d-flex flex-wrap mb-4">
        <v-card outlined class="pa-3 mr-3 mb-2 summary-card">
          <div class="text-caption grey--text">Videos</div>
          <div class="text-h6">{{ videos.length }}</div>
        </v-card>
        <v-card outlined class="pa-3 mr-3 mb-2 summary-card">
          <div class="text-caption grey--text">Teams</div>
          <div class="text-h6">{{ teamCount }}</div>
        </v-card>
        <v-card outlined class="pa-3 mb-2 summary-card">
          <div class="text-caption grey--text">Total duration</div>
          <div class="text-h6">{{ formatDuration(totalDuration) }}</div>
        </v-card>
      </div>

      <div v-if="loading" class="d-flex justify-center py-10">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="!filteredVideos.length" type="info" text dense>
        No self uploaded videos found.
      </v-alert>

      <v-row v-else>
        <v-col
          v-for="video in filteredVideos"
          :key="videoKey(video)"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card outlined class="video-card">
            <v-img
              v-if="video.thumb_url && !thumbnailFailed(video)"
              :src="video.thumb_url"
              :lazy-src="video.lazy_thumb_url || undefined"
              height="180"
              class="video-thumbnail"
              @error="markThumbnailBroken(video)"
            />
            <div
              v-else
              class="video-thumbnail-placeholder d-flex flex-column align-center justify-center text-caption white--text text-center px-2"
            >
              <div>{{ video.thumb_url ? 'Broken thumb_url' : 'Missing thumb_url' }}</div>
              <div v-if="video.thumb_url" class="thumbnail-url-hint">
                {{ video.thumb_url }}
              </div>
            </div>

            <v-card-text class="pb-2">
              <div class="font-weight-medium text-truncate" :title="video.title">
                {{ video.title || '-' }}
              </div>
              <div class="text-caption grey--text text--darken-1">
                {{ video.team_name || '-' }} · team {{ video.team_id || '-' }}
              </div>

              <div class="mt-3 d-flex flex-wrap">
                <v-chip x-small label outlined class="mr-1 mb-1">
                  {{ formatDuration(video.duration) }}
                </v-chip>
                <v-chip x-small label outlined class="mr-1 mb-1">
                  {{ formatBytes(video.original_size) }}
                </v-chip>
                <v-chip x-small label outlined class="mr-1 mb-1">
                  {{ formatDate(video.created_at) }}
                </v-chip>
              </div>

              <div class="mt-2 text-caption grey--text text--darken-1">
                Uploaded by {{ video.uploaded_by }}
              </div>
              <div class="text-caption grey--text text--darken-1">
                id {{ video.id }}
              </div>
            </v-card-text>

            <v-card-actions>
          <v-btn
                v-if="video.video_path"
                small
            text
            color="primary"
                :to="video.video_path"
          >
            Open
          </v-btn>
          <v-btn
                v-if="video.original_url"
                small
            text
            color="primary"
                :href="video.original_url"
            target="_blank"
          >
            Source
          </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import ax from '@/utils/axios/axios'
import { mapGetters } from 'vuex'

const ALLOWED_EMAIL = 'aku@kettunen.com'

export default {
  created() {
    if(this.hasAccess) this.fetchVideos()
  },
  data: () => ({
    loading: false,
    videos: [],
    brokenThumbnailIds: {},
    search: '',
    errorMessage: ''
  }),
  computed: {
    ...mapGetters('user', [
      'user'
    ]),
    hasAccess() {
      return this.user?.email === ALLOWED_EMAIL
    },
    teamCount() {
      return new Set(this.videos.map(video => video.team_id).filter(Boolean)).size
    },
    totalDuration() {
      return this.videos.reduce((total, video) => total + (Number(video.duration) || 0), 0)
    },
    filteredVideos() {
      const search = (this.search || '').toLowerCase()
      const videos = [...this.videos].sort((a, b) => {
        return this.dateValue(b.created_at) - this.dateValue(a.created_at)
      })

      if(!search) return videos

      return videos.filter(video => {
        return [
          video.title,
          video.id,
          video.team_name,
          video.team_id,
          video.uploaded_by,
          video.created_at
        ].join(' ').toLowerCase().includes(search)
      })
    }
  },
  methods: {
    async fetchVideos() {
      this.loading = true
      this.errorMessage = ''
      this.brokenThumbnailIds = {}

      try {
        const { data } = await this.getSelfUploadedVideos()
        this.videos = this.normalizeVideos(data)
      } catch(e) {
        this.errorMessage = e?.response?.data?.message || e?.message || 'Could not load self uploaded videos.'
      } finally {
        this.loading = false
      }
    },
    async getSelfUploadedVideos() {
      const endpoints = [
        '/admin/self-uploaded-videos',
        '/admin/videos/self-uploaded'
      ]

      let lastError
      for(const endpoint of endpoints) {
        try {
          return await ax.get(endpoint)
        } catch(e) {
          lastError = e
          if(e?.response?.status !== 404) throw e
        }
      }

      throw lastError
    },
    normalizeVideos(data) {
      if(Array.isArray(data?.teams)) {
        return data.teams.flatMap(team => this.normalizeTeamVideos(team))
      }

      if(Array.isArray(data?.videos)) {
        return data.videos.map(video => this.normalizeVideo(video))
      }

      if(Array.isArray(data)) {
        const groupedByTeam = data.some(item => Array.isArray(item.videos))
        if(groupedByTeam) return data.flatMap(team => this.normalizeTeamVideos(team))

        return data.map(video => this.normalizeVideo(video))
      }

      return []
    },
    normalizeTeamVideos(team) {
      return (team.videos || []).map(video => this.normalizeVideo({
        ...video,
        team_id: video.team_id || team.id || team.team_id,
        team_name: video.team_name || team.team_name || team.name
      }))
    },
    normalizeVideo(video) {
      const id = video.id || video.video_id
      const team = video.team || {}

      return {
        ...video,
        id,
        title: video.title || video.video_title || video.name,
        thumb_url: this.normalizeUrl(video.thumb_url),
        lazy_thumb_url: this.normalizeUrl(video.lazy_thumb_url),
        team_id: video.team_id || team.id,
        team_name: video.team_name || team.team_name || team.name,
        uploaded_by: video.uploaded_by || video.uploader_email || video.user_email || video.email || '-',
        duration: video.duration,
        original_size: video.original_size || video.size,
        original_url: video.original_url,
        created_at: video.created_at || video.created || video.createdAt,
        video_path: id ? `/videos/${String(id).split('/')[0]}` : ''
      }
    },
    normalizeUrl(url) {
      if(!url) return ''
      if(typeof url !== 'string') return ''
      if(url.startsWith('//')) return `https:${url}`
      if(url.includes('https')) return url

      return url.replace('http', 'https')
    },
    thumbnailFailed(item) {
      return !!this.brokenThumbnailIds[this.videoKey(item)]
    },
    markThumbnailBroken(item) {
      this.brokenThumbnailIds = {
        ...this.brokenThumbnailIds,
        [this.videoKey(item)]: true
      }
    },
    videoKey(video) {
      return video.id || `${video.team_id}-${video.title}-${video.created_at}`
    },
    dateValue(value) {
      if(!value) return 0

      const date = new Date(value)
      if(Number.isNaN(date.getTime())) return 0

      return date.getTime()
    },
    formatDuration(seconds) {
      const value = Number(seconds)
      if(!value) return '-'

      const hours = Math.floor(value / 3600)
      const minutes = Math.floor((value % 3600) / 60)
      const rest = Math.floor(value % 60).toString().padStart(2, '0')

      if(hours) return `${hours}:${minutes.toString().padStart(2, '0')}:${rest}`
      return `${minutes}:${rest}`
    },
    formatBytes(bytes) {
      const value = Number(bytes)
      if(!value) return '-'

      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
      const normalized = value / Math.pow(1024, exponent)

      return `${normalized.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`
    },
    formatDate(value) {
      if(!value) return '-'

      const date = new Date(value)
      if(Number.isNaN(date.getTime())) return value

      return date.toLocaleString()
    }
  }
}
</script>

<style scoped>
.summary-card {
  min-width: 150px;
}

.video-thumbnail {
  border-radius: 6px;
}

.video-thumbnail-placeholder {
  background: var(--v-primary-base);
  border-radius: 6px;
  height: 180px;
  overflow: hidden;
}

.thumbnail-url-hint {
  font-size: 9px;
  line-height: 1.1;
  max-width: 118px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
